import { keyframes, css } from 'styled-components';

// Палитра ссылается на CSS-переменные, чтобы тему можно было переключать
// на лету через data-theme на <html> без пересборки styled-компонентов.
// Конкретные значения переменных для тёмной/светлой темы заданы в GlobalStyle.
export const techColors = {
  neonGreen: 'var(--tc-neon-green)',
  neonBlue: 'var(--tc-neon-blue)',
  neonCyan: 'var(--tc-neon-cyan)',
  neonPurple: 'var(--tc-neon-purple)',
  neonPink: 'var(--tc-neon-pink)',
  darkBg: 'var(--tc-bg)',
  darkBgSecondary: 'var(--tc-bg-secondary)',
  darkBgTertiary: 'var(--tc-bg-tertiary)',
  darkBgMid: 'var(--tc-bg-mid)',
  terminalGreen: 'var(--tc-terminal-green)',
  terminalCyan: 'var(--tc-terminal-cyan)',
  terminalYellow: 'var(--tc-terminal-yellow)',
  textPrimary: 'var(--tc-text-primary)',
  textSecondary: 'var(--tc-text-secondary)',
  danger: 'var(--tc-danger)',
  dangerHover: 'var(--tc-danger-hover)',
  success: 'var(--tc-success)',
  offline: 'var(--tc-offline)',
  online: 'var(--tc-online)',
  offlineStatus: 'var(--tc-offline-status)',
  statusHidden: 'var(--tc-status-hidden)',
  readReceipt: 'var(--tc-read-receipt)',
  textGlow: 'var(--tc-text-glow)',
  borderGlow: 'var(--tc-border-glow)',
  borderGlowBlue: 'var(--tc-border-glow-blue)',
  borderSubtle: 'var(--tc-border-subtle)',
  borderMuted: 'var(--tc-border-muted)',
  featureChat: 'var(--tc-feature-chat)',
  featureCall: 'var(--tc-feature-call)',
  featureRooms: 'var(--tc-feature-rooms)',
  featureE2e: 'var(--tc-feature-e2e)',
};

// Значения переменных по темам. Используются GlobalStyle для генерации
// :root (dark по умолчанию) и [data-theme="light"].
export const THEME_PALETTES = {
  dark: {
    '--tc-neon-green': '#e0e0e0',
    '--tc-neon-blue': '#ffffff',
    '--tc-neon-cyan': '#ffffff',
    '--tc-neon-purple': '#ffffff',
    '--tc-neon-pink': '#cccccc',
    '--tc-bg': '#0a0a0a',
    '--tc-bg-secondary': '#141414',
    '--tc-bg-tertiary': '#1f1f1f',
    '--tc-bg-mid': '#101010',
    '--tc-terminal-green': '#f0f0f0',
    '--tc-terminal-cyan': '#ffffff',
    '--tc-terminal-yellow': '#b0b0b0',
    '--tc-text-primary': '#f0f0f0',
    '--tc-text-secondary': '#888888',
    '--tc-danger': '#ef4444',
    '--tc-danger-hover': '#dc2626',
    '--tc-success': '#22c55e',
    '--tc-offline': '#505050',
    '--tc-online': '#22c55e',
    '--tc-offline-status': '#ef4444',
    '--tc-status-hidden': '#ffffff',
    '--tc-read-receipt': '#7ec8ff',
    '--tc-text-glow': 'rgba(255, 255, 255, 1)',
    '--tc-border-glow': 'rgba(255, 255, 255, 0.25)',
    '--tc-border-glow-blue': 'rgba(255, 255, 255, 0.25)',
    '--tc-border-subtle': 'rgba(255, 255, 255, 0.12)',
    '--tc-border-muted': 'rgba(255, 255, 255, 0.08)',
    '--tc-feature-chat': '#7dd3fc',
    '--tc-feature-call': '#2dd4bf',
    '--tc-feature-rooms': '#f59e0b',
    '--tc-feature-e2e': '#22c55e',
  },
  light: {
    '--tc-neon-green': '#2a2a2a',
    '--tc-neon-blue': '#0b0b0b',
    '--tc-neon-cyan': '#111111',
    '--tc-neon-purple': '#111111',
    '--tc-neon-pink': '#333333',
    '--tc-bg': '#f4f4f5',
    '--tc-bg-secondary': '#ffffff',
    '--tc-bg-tertiary': '#e6e6e8',
    '--tc-bg-mid': '#ececee',
    '--tc-terminal-green': '#0a0a0a',
    '--tc-terminal-cyan': '#111111',
    '--tc-terminal-yellow': '#555555',
    '--tc-text-primary': '#111214',
    '--tc-text-secondary': '#5f6368',
    '--tc-danger': '#dc2626',
    '--tc-danger-hover': '#b91c1c',
    '--tc-success': '#16a34a',
    '--tc-offline': '#b0b0b0',
    '--tc-online': '#16a34a',
    '--tc-offline-status': '#dc2626',
    '--tc-status-hidden': '#111111',
    '--tc-read-receipt': '#2563eb',
    '--tc-text-glow': 'transparent',
    '--tc-border-glow': 'rgba(0, 0, 0, 0.22)',
    '--tc-border-glow-blue': 'rgba(0, 0, 0, 0.22)',
    '--tc-border-subtle': 'rgba(0, 0, 0, 0.14)',
    '--tc-border-muted': 'rgba(0, 0, 0, 0.09)',
    '--tc-feature-chat': '#0284c7',
    '--tc-feature-call': '#0d9488',
    '--tc-feature-rooms': '#d97706',
    '--tc-feature-e2e': '#16a34a',
  },
};

// Преобразует цвет + hex-альфу (напр. '30') в color-mix, чтобы работало
// с CSS-переменными (обычная конкатенация ${color}30 ломала бы var(...)).
export const alpha = (color = techColors.neonCyan, hexAlpha = 'ff') => {
  const parsed = parseInt(hexAlpha, 16);
  const ratio = Number.isNaN(parsed) ? 1 : parsed / 255;
  const pct = Math.max(0, Math.min(100, Math.round(ratio * 100)));
  return `color-mix(in srgb, ${color} ${pct}%, transparent)`;
};

// Анимации
export const scanlineAnimation = keyframes`
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100vh);
  }
`;

export const flickerAnimation = keyframes`
  0%, 100% {
    opacity: 1;
  }
  41.99% {
    opacity: 1;
  }
  42% {
    opacity: 0;
  }
  43% {
    opacity: 0;
  }
  43.01% {
    opacity: 1;
  }
  45.99% {
    opacity: 1;
  }
  46% {
    opacity: 0;
  }
  46.01% {
    opacity: 1;
  }
  51.99% {
    opacity: 1;
  }
  52% {
    opacity: 0;
  }
  52.01% {
    opacity: 1;
  }
`;

export const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 5px ${alpha(techColors.neonCyan, '80')}, 0 0 10px ${alpha(techColors.neonCyan, '60')}, 0 0 15px ${alpha(techColors.neonCyan, '40')};
  }
  50% {
    box-shadow: 0 0 10px ${alpha(techColors.neonCyan, '80')}, 0 0 20px ${alpha(techColors.neonCyan, '60')}, 0 0 30px ${alpha(techColors.neonCyan, '40')};
  }
`;

export const glowPulseBlue = keyframes`
  0%, 100% {
    box-shadow: 0 0 5px ${alpha(techColors.neonBlue, '80')}, 0 0 10px ${alpha(techColors.neonBlue, '60')}, 0 0 15px ${alpha(techColors.neonBlue, '40')};
  }
  50% {
    box-shadow: 0 0 10px ${alpha(techColors.neonBlue, '80')}, 0 0 20px ${alpha(techColors.neonBlue, '60')}, 0 0 30px ${alpha(techColors.neonBlue, '40')};
  }
`;

export const typingAnimation = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

export const blinkCursor = keyframes`
  0%, 49% {
    opacity: 1;
  }
  50%, 100% {
    opacity: 0;
  }
`;

export const matrixRain = keyframes`
  0% {
    transform: translateY(-100%);
    opacity: 1;
  }
  100% {
    transform: translateY(100vh);
    opacity: 0;
  }
`;

// Эффекты для компонентов
export const scanlineEffect = css`
  &::after {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(
      to bottom,
      transparent,
      rgba(255, 255, 255, 0.06),
      transparent
    );
    pointer-events: none;
    z-index: 9999;
    animation: ${scanlineAnimation} 8s linear infinite;
  }
`;

export const crtFlicker = css`
  animation: ${flickerAnimation} 0.15s infinite;
`;

export const terminalBorder = (color = techColors.neonCyan) => `
  border: 1px solid ${techColors.borderSubtle};
`;

export const neonGlow = (color = techColors.neonCyan, intensity = 'normal') => {
  const styles = {
    subtle: `box-shadow: 0 0 3px ${alpha(color, '50')}, 0 0 6px ${alpha(color, '30')};`,
    normal: `box-shadow: 0 0 5px ${alpha(color, '60')}, 0 0 10px ${alpha(color, '40')}, 0 0 15px ${alpha(color, '20')};`,
    strong: `box-shadow: 0 0 8px ${color}, 0 0 15px ${alpha(color, '80')}, 0 0 25px ${alpha(color, '40')};`,
  };
  return styles[intensity] || styles.normal;
};

export const dangerButtonDefaultGlow = `
  ${neonGlow(techColors.danger, 'normal')}
`;

export const dangerButtonHoverStyles = `
  transform: translateY(-2px);
  ${neonGlow(techColors.danger, 'strong')}
`;

export const positiveButtonDefaultGlow = `
  ${neonGlow(techColors.neonCyan, 'normal')}
`;

export const positiveButtonHoverStyles = `
  transform: translateY(-2px);
  ${neonGlow(techColors.neonCyan, 'strong')}
`;

// Свечение текста берёт цвет из отдельной переменной --tc-text-glow, а не из
// переданного неонового цвета: в светлой теме переменная прозрачна, поэтому
// ореол вокруг заголовков полностью выключается. Аргумент оставлен ради
// обратной совместимости с вызовами, но намеренно не используется.
export const textGlow = () => `
  text-shadow: 0 0 5px ${techColors.textGlow}, 0 0 10px ${alpha(techColors.textGlow, '80')};
`;

export const textGlowStrong = () => `
  text-shadow: 0 0 8px ${techColors.textGlow}, 0 0 15px ${techColors.textGlow}, 0 0 25px ${alpha(techColors.textGlow, '80')};
`;

export const terminalTextStyle = `
  font-family: 'JetBrains Mono', monospace;
  font-weight: 400;
  letter-spacing: 0.5px;
`;

export const terminalHeaderStyle = `
  font-family: 'JetBrains Mono', monospace;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

export const retroGridPattern = `
  background-image: 
    linear-gradient(${alpha(techColors.neonCyan, '20')} 1px, transparent 1px),
    linear-gradient(90deg, ${alpha(techColors.neonCyan, '20')} 1px, transparent 1px);
  background-size: 20px 20px;
`;

export const retroDotPattern = `
  background-image: radial-gradient(circle, ${alpha(techColors.neonCyan, '30')} 1px, transparent 1px);
  background-size: 15px 15px;
`;

export const terminalCursor = css`
  &::after {
    content: '_';
    animation: ${blinkCursor} 1s infinite;
    color: ${techColors.neonCyan};
  }
`;

export const typingEffect = (duration = '3s') => css`
  overflow: hidden;
  white-space: nowrap;
  animation: ${typingAnimation} ${duration} steps(40, end);
`;
